import { useEffect, useState, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { Badge, Box, ClickAwayListener, Paper, Popper, Tooltip, useMediaQuery } from '@mui/material';
import { BellOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Channel, Event } from 'stream-chat';
import { Chat, Channel as StreamChannel, MessageList, Window } from 'stream-chat-react';
import 'stream-chat-react/dist/css/v2/index.css';
import useAuth from 'hooks/useAuth';
import { Rights, User } from 'graphql/types';
import { useStreamChat } from 'contexts/StreamChatContext';
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { useDispatch } from 'react-redux';
import { openSnackbar } from 'store/reducers/snackbar';
import { stripMarkdownLinks, truncateText } from 'utils/markdown';

// Add custom styles for Stream Chat
const styles = `
  .str-chat__container {
    height: 400px !important;
  }
  .str-chat__main-panel {
    height: 100% !important;
  }
  .str-chat__message-list {
    height: 100% !important;
    overflow-y: auto !important;
  }
`;

const StreamNotification = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  const { client, isConnected, isLoading: clientLoading } = useStreamChat();

  const anchorRef = useRef<any>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalMessageCount, setTotalMessageCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState<Channel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { user: currentUser } = useAuth();
  const dispatch = useDispatch();
  const initNotificationChannel = async () => {
    try {
      setError(null);
      const channelId = `notifications-${currentUser?.id}`;

        try {
          const channelResponse = await client.queryChannels({
            type: 'messaging',
            id: channelId
          });

          if (!channelResponse || channelResponse.length === 0) {
            setChannel(null);
            setTotalMessageCount(0);
            setUnreadCount(0);
            return;
          }

          const existingChannel = channelResponse[0];
          setChannel(existingChannel);

          setUnreadCount(existingChannel.countUnread());

          const channelState = existingChannel.state;
          const messages = channelState.messages || [];
          setTotalMessageCount(messages.length);

          existingChannel.on('message.new', (event: Event) => {
            const newUnreadCount = existingChannel.countUnread();
            setUnreadCount(newUnreadCount);

            const updatedMessages = existingChannel.state.messages || [];
            setTotalMessageCount(updatedMessages.length);

            const messageText = event.message?.text || '';
            const cleanMessage = stripMarkdownLinks(messageText);
            const truncatedMessage = truncateText(cleanMessage, 200);
            dispatch(
              openSnackbar({
                open: true,
                message: `${truncatedMessage}`,
                variant: 'alert',
                alert: { color: 'info' },
                close: true
              })
            );
          });

          existingChannel.on('message.read', () => {
            setUnreadCount(existingChannel.countUnread());
          });

        existingChannel.on('message.deleted', () => {
         const updatedMessages = existingChannel.state.messages || [];
          setTotalMessageCount(updatedMessages.length);
        });
      } catch (channelError) {
        setChannel(null);
        setTotalMessageCount(0);
        setUnreadCount(0);
      }
    } catch (error) {
      setError('Failed to initialize notifications');
    }
  };


  useEffect(() => {
    if (!client || !isConnected || !currentUser) {
      setChannel(null);
      setTotalMessageCount(0);
      setUnreadCount(0);
      return;
    }
    const handleStreamReady = () => {
      initNotificationChannel();
    };
    if (isConnected) {
      handleStreamReady();
    }
    client.on("connection.recovered", handleStreamReady);
    client.on("connection.changed", handleStreamReady);

    return () => {
      client.off("connection.recovered", handleStreamReady);
      client.off("connection.changed", handleStreamReady);
    };
  }, [client, isConnected, currentUser?.id, dispatch]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
    // Mark messages as read when closing the notification panel
    if (channel) {
      channel.markRead();
      setUnreadCount(0);
    }
  };

  const iconBackColorOpen = theme.palette.mode === 'dark' ? 'grey.200' : 'grey.300';

  // Don't render anything if loading, error, no channel, or no messages
  if (clientLoading || error || !client || !currentUser) {
    return null;
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={{ color: 'text.primary', bgcolor: open ? iconBackColorOpen : 'transparent' }}
        aria-label="open notifications"
        ref={anchorRef}
        aria-controls={open ? 'notifications-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [matchesXs ? -5 : 0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: '100%',
                minWidth: 285,
                maxWidth: 420,
                [theme.breakpoints.down('md')]: {
                  maxWidth: 285
                }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title={`Notifications${unreadCount > 0 ? ` (${unreadCount})` : ''}`}
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {unreadCount > 0 && (
                        <Tooltip title="Mark as all read">
                          <IconButton
                            color="success"
                            size="small"
                            onClick={() => {
                              if (channel) {
                                channel.markRead();
                                setUnreadCount(0);
                              }
                            }}
                          >
                            <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  {client && channel && totalMessageCount > 0 &&(
                    <>
                      <style>{styles}</style>
                      <Chat client={client} theme="str-chat__theme-dark">
                        <StreamChannel channel={channel}>
                          <Window>
                            <MessageList
                              messageActions={[]}
                              disableDateSeparator={false}
                              threadList={false}
                              groupStyles={() => 'single'}
                              additionalMessageInputProps={{
                                focus: false
                              }}
                            />
                          </Window>
                        </StreamChannel>
                      </Chat>
                    </>
                  )}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default StreamNotification;
